import React, { FC, ReactNode } from 'react'

import { styled } from '@linaria/react'
import { FormattedMessage } from 'react-intl'

import { TextWaveUnderline } from 'components/designSystem/TextWaveUnderline/TextWaveUnderline'
import { Typography } from 'components/designSystem/Typography/Typography'
import { sortGendersLgbtFeature45 } from 'components/page/Profile/function/sortGendersLgbtFeature45'
import { GenderFieldLexeme } from 'components/page/ProfileCommon/ProfileCommon.types'
import { useModifyLgbtLexemes } from 'components/page/ProfileCommon/ProfileField/ProfileLookFor.useModifyLgbtLexemes'
import {
  GQLDatingProfileLexicalOptionType,
  GQLGender,
  GQLLookFor,
} from 'generated-graphql/graphql'
import { useLgbtDetail } from 'hooks/useLgbtDetail'

export const ProfileTarget: FC<{
  targetGender: GQLLookFor[]
  targetStartAge: number
  targetFinishAge: number
  userGender: GQLGender
  lexemes: GQLDatingProfileLexicalOptionType[]
  body: ReactNode
  lgbtHidden?: boolean
  lgbtModified?: boolean
}> = ({
  targetGender,
  targetStartAge,
  targetFinishAge,
  userGender,
  lexemes,
  body,
  lgbtHidden,
  lgbtModified,
}) => {
  const { lgbtRestriction } = useLgbtDetail(userGender)

  const _lexemes = useModifyLgbtLexemes(lexemes, userGender)

  const _targetGender = lgbtRestriction
    ? sortGendersLgbtFeature45(targetGender, userGender)
    : targetGender

  return (
    <Parent>
      <TextWaveUnderline
        fontSize={16}
        title={
          <FormattedMessage
            id="dating_profile.dating_goals"
            defaultMessage="Цель знакомства"
          />
        }
        data-name="goal-title-section"
      />
      <Typography fontSize={24} fontWeight={500}>
        {!lgbtHidden && (
          <>
            <span data-name="look-for">
              <TargetGender
                targetGender={_targetGender}
                lexemes={_lexemes}
                userGender={userGender}
                lgbtModified={lgbtModified}
              />
            </span>
            <> </>
          </>
        )}
        <nobr>
          <span data-name="look-for-age-range">
            <TargetAge
              targetStartAge={targetStartAge}
              targetFinishAge={targetFinishAge}
            />
          </span>
        </nobr>
      </Typography>
      {body}
    </Parent>
  )
}

const TargetAge: FC<{ targetStartAge: number; targetFinishAge: number }> = ({
  targetStartAge,
  targetFinishAge,
}) => {
  if (targetStartAge === targetFinishAge) {
    return (
      <FormattedMessage
        id="target.range.same.years"
        defaultMessage="{targetAge} лет"
        values={{
          targetAge: targetStartAge,
        }}
      />
    )
  }

  return (
    <FormattedMessage
      id="target.range.years"
      defaultMessage="{targetStartAge}{dash}{targetFinishAge} лет"
      values={{
        targetStartAge,
        targetFinishAge,
        dash: <>&ndash;</>,
      }}
    />
  )
}

const TargetGender: FC<{
  targetGender: GQLLookFor[]
  userGender: GQLGender
  lexemes: GQLDatingProfileLexicalOptionType[]
  lgbtModified?: boolean
}> = ({ targetGender, userGender, lgbtModified, lexemes }) => {
  const target = targetGender.reduce((acc, gender) => {
    const _lexeme = lexemes.find(({ value }) => value === gender)

    if (_lexeme) {
      return [...acc, _lexeme[GenderFieldLexeme[userGender]]?.toLowerCase()]
    }

    return acc
  }, [])

  const lexemeStart = lgbtModified ? (
    <FormattedMessage id="search.for" defaultMessage="Ищу" />
  ) : (
    <FormattedMessage id="look.for.foreign" defaultMessage="Интересует" />
  )

  if (target.length > 1) {
    return (
      <FormattedMessage
        id="app.search.gender.target.multiple.update"
        defaultMessage="{lexemeStart} {target1} <nobr>или {target2}</nobr>, "
        values={{
          target1: target[0],
          target2: target[1],
          nobr: (chunks) => <nobr>{chunks}</nobr>,
          lexemeStart,
        }}
      />
    )
  }

  return (
    <FormattedMessage
      id="app.search.gender.targe.single.update"
      defaultMessage="{lexemeStart} {target}, "
      values={{
        target: target[0],
        lexemeStart,
      }}
    />
  )
}

const Parent = styled.div`
  display: grid;
  gap: var(--spacing-16px);
  grid-gap: var(--spacing-16px);
`
